import { Button, Form, Input, Typography, message } from 'antd'
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import logo from '../images/logoblank.png'
import styles from './auth.module.css'
import { auth } from './firebase.jsx'

const { Text } = Typography

const SESSION_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const MAX_LOGIN_ATTEMPTS = 5
const LOCKOUT_DURATION_MS = 15 * 60 * 1000 // 15 minutes
const LOGIN_ATTEMPTS_KEY = 'loginAttempts'
const SESSION_START_KEY = 'sessionStartTime'

function getLoginAttempts() {
  try {
    const data = localStorage.getItem(LOGIN_ATTEMPTS_KEY)
    if (!data) return { count: 0, firstAttemptTime: null }
    const parsed = JSON.parse(data)
    // Reset if lockout period has passed
    if (parsed.firstAttemptTime && Date.now() - parsed.firstAttemptTime > LOCKOUT_DURATION_MS) {
      localStorage.removeItem(LOGIN_ATTEMPTS_KEY)
      return { count: 0, firstAttemptTime: null }
    }
    return parsed
  } catch {
    return { count: 0, firstAttemptTime: null }
  }
}

function recordFailedAttempt() {
  const attempts = getLoginAttempts()
  const newAttempts = {
    count: attempts.count + 1,
    firstAttemptTime: attempts.firstAttemptTime || Date.now(),
  }
  localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(newAttempts))
  return newAttempts
}

function clearLoginAttempts() {
  localStorage.removeItem(LOGIN_ATTEMPTS_KEY)
}

function isLockedOut() {
  const attempts = getLoginAttempts()
  if (attempts.count >= MAX_LOGIN_ATTEMPTS) {
    const timeRemaining = LOCKOUT_DURATION_MS - (Date.now() - attempts.firstAttemptTime)
    if (timeRemaining > 0) {
      return Math.ceil(timeRemaining / 60000) // Return minutes remaining
    }
    clearLoginAttempts()
  }
  return false
}

function Auth() {
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const history = useNavigate()
  const sessionTimeoutRef = useRef(null)
  const activityTimeoutRef = useRef(null)
  const sessionCleanupRef = useRef(null)

  const handleLogout = useCallback(async () => {
    try {
      await auth.signOut()
      localStorage.removeItem(SESSION_START_KEY)
      localStorage.removeItem('sessionId')
      history('/login')
    } catch (err) {
      console.error('Logout error:', err)
    }
  }, [history])

  const resetActivityTimer = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current)
    }
    activityTimeoutRef.current = setTimeout(() => {
      message.warning('Session expired due to inactivity.')
      handleLogout()
    }, SESSION_TIMEOUT_MS)
  }, [handleLogout])

  const startSessionTimeout = useCallback(() => {
    localStorage.setItem(SESSION_START_KEY, Date.now().toString())
    resetActivityTimer()

    // Listen for user activity to reset timer
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    const handleActivity = () => resetActivityTimer()
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current)
    }
  }, [resetActivityTimer])

  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current)
      if (sessionCleanupRef.current) sessionCleanupRef.current()
    }
  }, [])

  const onFinish = async (values) => {
    const lockoutMinutes = isLockedOut()
    if (lockoutMinutes) {
      setError(`Too many failed attempts. Please try again in ${lockoutMinutes} minute(s).`)
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      await signInWithEmailAndPassword(auth, values.email, values.password)
      clearLoginAttempts()
      sessionCleanupRef.current = startSessionTimeout()
      history('/campaign-tools')
    } catch (err) {
      const attempts = recordFailedAttempt()
      const remainingAttempts = MAX_LOGIN_ATTEMPTS - attempts.count
      if (remainingAttempts > 0) {
        setError(`Invalid credentials. ${remainingAttempts} attempt(s) remaining.`)
      } else {
        setError('Too many failed attempts. Account locked for 15 minutes.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePasswordReset = async () => {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      message.error('Please enter your email address.')
      return
    }

    try {
      await sendPasswordResetEmail(auth, trimmedEmail)
      message.success('Password reset email sent. Please check your inbox.')
    } catch (err) {
      message.error('Failed to send reset email. Please check the email address.')
    }
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.authHeader}>
        <img src={logo} alt="Bernoulli Technologies Logo" className={styles.authLogo} />
        <span className={styles.authTitle}>Account Login</span>
      </div>
      <div className={styles.authContainer}>
        <Form
          name="signin"
          onFinish={onFinish}
          layout="vertical"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email address!' }]}
          >
            <Input autoComplete="on" onChange={(e) => setEmail(e.target.value)} />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: 'Please enter your password!',
              },
            ]}
          >
            <Input.Password autoComplete="" name='password' />
          </Form.Item>
          {error && <div className={styles.error}>{error}</div>}
          <div>
            <Button type="link" onClick={handlePasswordReset} className={styles.authLink}>
              Forgot Password?
            </Button>
          </div>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
              Sign In
            </Button>
          </Form.Item>
        </Form>
        <Text>
        Don&apos;t have an account? <Link to="/signup" className={styles.authLink}>Sign Up</Link>
        </Text>
      </div>
    </div>

  )
}

export default Auth
